export type SectionType = "ai" | "static";
export type PlaceholderKind = "GENERATED_CONTENT" | "none";

export interface TemplateSection {
  id: string;
  title: string;
  headingLevel: 1 | 2 | 3 | 4 | 5 | 6;
  /** When false, markdown omits the # heading line (Normal text in the editor). */
  useSectionHeading?: boolean;
  sectionType: SectionType;
  prompt: string;
  assignedTopics: string[];
  textPrefix: string;
  placeholder: PlaceholderKind;
  guidanceComment?: string;
  rawBlock?: string;
  activeTab?: number;
}

export interface ParsedApiSection {
  sectionId?: string;
  displayName?: string;
  type?: string;
  htmlStructure?: string;
  assignedMainTopics?: string[];
  promptTemplate?: string;
  description?: string;
}

export const TOPIC_DESCRIPTIONS_TOKEN = "{TOPIC_DESCRIPTIONS}";
export const TOPIC_SCOPE_PHRASE = "Scope is based on";

const TOPIC_DESCRIPTIONS_RE = /\{TOPIC_DESCRIPTIONS\}/gi;
const TOPIC_SCOPE_APPENDIX_RE = /\s*Scope is based on\s*\{TOPIC_DESCRIPTIONS\}/gi;

export function topicDescriptionsAppendix(): string {
  return `${TOPIC_SCOPE_PHRASE} ${TOPIC_DESCRIPTIONS_TOKEN}`;
}

export function stripTopicDescriptionsToken(prompt: string): string {
  return prompt
    .replace(TOPIC_SCOPE_APPENDIX_RE, "")
    .replace(TOPIC_DESCRIPTIONS_RE, "")
    .replace(/\s*Scope is based on\s*$/i, "")
    .replace(/\s{2,}/g, " ");
}

/** Prompt written to markdown — appends scope + topic placeholder when topics are assigned. */
export function serializePromptForMarkdown(section: TemplateSection): string {
  const base = stripTopicDescriptionsToken(section.prompt).trim();
  if (
    section.sectionType !== "ai" ||
    !base ||
    !section.assignedTopics.length
  ) {
    return base;
  }
  if (base.toLowerCase().endsWith(TOPIC_SCOPE_PHRASE.toLowerCase())) {
    return `${base} ${TOPIC_DESCRIPTIONS_TOKEN}`;
  }
  return `${base} ${topicDescriptionsAppendix()}`;
}

export function sectionEditorPromptValue(section: TemplateSection): string {
  return stripTopicDescriptionsToken(section.prompt);
}

/** Editor rule: instructions present → AI; empty → plain text. */
export function inferSectionTypeFromEditor(
  section: Pick<TemplateSection, "prompt">,
): SectionType {
  return stripTopicDescriptionsToken(section.prompt).trim() ? "ai" : "static";
}

export function applyInferredSectionType(section: TemplateSection): TemplateSection {
  const sectionType = inferSectionTypeFromEditor(section);
  return {
    ...section,
    sectionType,
    placeholder: sectionType === "ai" ? "GENERATED_CONTENT" : "none",
    assignedTopics: sectionType === "ai" ? section.assignedTopics : [],
  };
}

let sectionIdCounter = 0;
export function newSectionId(): string {
  sectionIdCounter += 1;
  return `sec-${Date.now()}-${sectionIdCounter}`;
}

export function createEmptySection(
  overrides?: Partial<TemplateSection>,
): TemplateSection {
  return {
    id: newSectionId(),
    title: "",
    headingLevel: 2,
    sectionType: "static",
    prompt: "",
    assignedTopics: [],
    textPrefix: "",
    placeholder: "none",
    useSectionHeading: true,
    activeTab: 0,
    ...overrides,
  };
}

const HEADING_RE = /^(#{1,6})\s+(.+)$/;

function headingLevelFromHashes(hashes: string): TemplateSection["headingLevel"] {
  const n = hashes.length;
  return (n >= 1 && n <= 6 ? n : 2) as TemplateSection["headingLevel"];
}

function extractPromptMeta(block: string) {
  const promptMatch = block.match(/<!--\s*PROMPT:\s*([\s\S]*?)-->/i);
  const prompt = promptMatch
    ? stripTopicDescriptionsToken(promptMatch[1].trim())
    : "";
  const topicsMatch = block.match(/<!--\s*ASSIGNED_TOPICS:\s*([^>]*)-->/i);
  const assignedTopics = topicsMatch
    ? topicsMatch[1].split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  const guidanceMatch = block.match(
    /<!--(?!\s*PROMPT:)(?!\s*ASSIGNED_TOPICS:)([\s\S]*?)-->/,
  );
  const guidanceComment = guidanceMatch
    ? `<!--${guidanceMatch[1]}-->`
    : undefined;
  return { prompt, assignedTopics, guidanceComment };
}

function detectPlaceholder(block: string): PlaceholderKind {
  if (/\[GENERATED_CONTENT\]/i.test(block)) return "GENERATED_CONTENT";
  return "none";
}

function stripMetaFromPrefix(prefix: string): string {
  let text = prefix.replace(/<!--[\s\S]*?-->/g, "");
  text = text.replace(/\[GENERATED_CONTENT\]/gi, "");
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

function deriveTitle(headingTitle: string, textPrefix: string, blockIndex: number): string {
  const subheading = textPrefix.match(/^#{2,6}\s+(.+?)(?:\n|$)/);
  if (subheading) return subheading[1].trim();
  if (blockIndex === 0) return headingTitle;
  const lines = textPrefix.split("\n").map((l) => l.trim()).filter(Boolean);
  const lastLine = lines[lines.length - 1] || "";
  const label = lastLine.replace(/:\s*$/, "").trim();
  return label && label.length < 80 ? label : headingTitle;
}

function stripSubheadingFromPrefix(textPrefix: string): string {
  return textPrefix.replace(/^#{2,6}\s+.+?(?:\n|$)/, "").trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** rawBlock often includes the section heading; avoid duplicating it on serialize. */
function stripDuplicateHeadingFromBlock(block: string, title: string): string {
  const t = title.trim();
  if (!t) return block.trim();
  const re = new RegExp(`^#{1,6}\\s+${escapeRegExp(t)}\\s*(?:\\n|$)`, "m");
  return block.replace(re, "").trim();
}

function normalizeTextPrefix(textPrefix: string, title: string): string {
  const text = textPrefix.trim();
  const t = title.trim();
  if (!text || !t) return text;
  if (text === t || text === `${t}:`) return "";
  const sub = text.match(/^(#{1,6})\s+(.+)$/);
  if (sub && sub[2].trim() === t) return "";
  return text;
}

function isSubheadingOnly(block: string): boolean {
  const t = block.trim();
  return (
    /^#{2,6}\s+\S/.test(t) &&
    !/<!--\s*PROMPT:/i.test(t) &&
    !/\[(GENERATED_CONTENT|USER_INPUT)\]/i.test(t)
  );
}

function isLabelOnly(block: string): boolean {
  const t = block.trim();
  return /^[^\n#<[\]]+:\s*$/.test(t);
}

function splitMixedUserBlocks(blocks: string[]): string[] {
  const out: string[] = [];
  for (const block of blocks) {
    if (!/\[USER_INPUT\]/i.test(block)) {
      out.push(block);
      continue;
    }
    const match = block.match(/^([\s\S]*?\[USER_INPUT\])\s*\n\n([\s\S]+)$/i);
    if (match) {
      const tail = match[2].trim();
      if (isLabelOnly(tail) || isSubheadingOnly(tail)) {
        out.push(match[1].trim());
        out.push(tail);
        continue;
      }
    }
    out.push(block);
  }
  return out;
}

function mergePrefixBlocks(blocks: string[]): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < blocks.length) {
    if (
      (isSubheadingOnly(blocks[i]) || isLabelOnly(blocks[i])) &&
      i + 1 < blocks.length
    ) {
      out.push(`${blocks[i].trim()}\n\n${blocks[i + 1].trim()}`);
      i += 2;
    } else {
      out.push(blocks[i]);
      i += 1;
    }
  }
  return out;
}

/** Plain text / guidance before a PROMPT belongs in the same section card. */
function isStandaloneBlock(block: string): boolean {
  return (
    /<!--\s*PROMPT:/i.test(block) ||
    /\[(?:GENERATED_CONTENT|USER_INPUT)\]/i.test(block)
  );
}

function mergeTextIntoFollowingPrompt(blocks: string[]): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < blocks.length) {
    if (
      !isStandaloneBlock(blocks[i]) &&
      i + 1 < blocks.length &&
      /<!--\s*PROMPT:/i.test(blocks[i + 1])
    ) {
      out.push(`${blocks[i].trim()}\n\n${blocks[i + 1].trim()}`);
      i += 2;
    } else {
      out.push(blocks[i]);
      i += 1;
    }
  }
  return out;
}

function defaultActiveTab(
  section: Pick<TemplateSection, "sectionType" | "prompt" | "assignedTopics" | "textPrefix">,
): number {
  if (section.sectionType === "ai") {
    if (section.prompt.trim() || section.assignedTopics.length) return 1;
    return 1;
  }
  if (section.textPrefix.trim()) return 1;
  return 0;
}

function extractBlocksFromBody(body: string): string[] {
  const trimmed = body.trim();
  if (!trimmed) return [];
  let blocks: string[] = [];
  let rest = trimmed;

  while (rest.length > 0) {
    const promptIdx = rest.search(/<!--\s*PROMPT:/i);
    if (promptIdx === -1) {
      blocks.push(rest.trim());
      break;
    }
    if (promptIdx > 0) blocks.push(rest.slice(0, promptIdx).trim());

    const fromPrompt = rest.slice(promptIdx);
    const afterPrompt = fromPrompt.replace(/^<!--\s*PROMPT:\s*[\s\S]*?-->\s*/i, "");
    const topicsMatch = afterPrompt.match(/^<!--\s*ASSIGNED_TOPICS:\s*[^>]*-->\s*/i);
    let consumed = fromPrompt.length - afterPrompt.length;
    if (topicsMatch) consumed += topicsMatch[0].length;

    const remainder = fromPrompt.slice(consumed);
    const phMatch = remainder.match(/^(\s*\[(?:GENERATED_CONTENT|USER_INPUT)\]\s*)/i);
    if (phMatch) consumed += phMatch[0].length;

    blocks.push(fromPrompt.slice(0, consumed).trim());
    rest = fromPrompt.slice(consumed).trim();
  }
  blocks = splitMixedUserBlocks(blocks);
  blocks = mergePrefixBlocks(blocks);
  blocks = mergeTextIntoFollowingPrompt(blocks);
  return blocks.filter(Boolean);
}

function parseBlockToSection(
  block: string,
  headingLevel: number,
  headingTitle: string,
  blockIndex: number,
): TemplateSection {
  const { prompt, assignedTopics, guidanceComment } = extractPromptMeta(block);
  const placeholder = detectPlaceholder(block);
  const sectionType = prompt.trim() ? "ai" : "static";
  const rawPrefix = stripMetaFromPrefix(block);
  const title = deriveTitle(headingTitle, rawPrefix, blockIndex);
  let textPrefix = stripSubheadingFromPrefix(rawPrefix);
  if (sectionType === "ai" && isLabelOnly(textPrefix)) {
    textPrefix = "";
  }
  if (
    sectionType === "ai" &&
    textPrefix.match(/^#{2,6}\s+(.+?)(?:\n|$)/)?.[1]?.trim() === title
  ) {
    textPrefix = "";
  }

  const section: TemplateSection = {
    id: newSectionId(),
    title,
    headingLevel: (blockIndex === 0
      ? headingLevel
      : Math.min(headingLevel + 1, 6)) as TemplateSection["headingLevel"],
    sectionType,
    prompt,
    assignedTopics,
    textPrefix,
    placeholder,
    guidanceComment,
    rawBlock: block,
    activeTab: 0,
  };
  section.activeTab = defaultActiveTab(section);
  return section;
}

/** Parse raw markdown into UI sections without rewriting structure. */
export function parseRawMarkdownToTemplateSections(
  markdown: string,
): TemplateSection[] {
  const lines = markdown.split("\n");
  const result: TemplateSection[] = [];
  let current: { level: number; title: string } | null = null;
  let bodyLines: string[] = [];

  const flush = () => {
    if (!current) return;
    const blocks = extractBlocksFromBody(bodyLines.join("\n"));
    if (!blocks.length) {
      result.push(
        createEmptySection({
          title: current.title,
          headingLevel: current.level as TemplateSection["headingLevel"],
          sectionType: "static",
          placeholder: "none",
          activeTab: 0,
        }),
      );
    } else {
      blocks.forEach((block, i) => {
        result.push(parseBlockToSection(block, current!.level, current!.title, i));
      });
    }
    bodyLines = [];
  };

  for (const line of lines) {
    const hm = line.match(HEADING_RE);
    if (hm) {
      const level = headingLevelFromHashes(hm[1]);
      if (current === null) {
        current = { level, title: hm[2].trim() };
      } else if (level <= current.level) {
        flush();
        current = { level, title: hm[2].trim() };
      } else {
        bodyLines.push(line);
      }
    } else if (current) {
      bodyLines.push(line);
    }
  }
  flush();
  const sections = result.length ? result : [createEmptySection()];
  return normalizeTemplateSections(sections);
}

/** [USER_INPUT] is plain text — migrate legacy user-input sections on load. */
export function normalizeTemplateSection(section: TemplateSection): TemplateSection {
  const legacyUser =
    (section as { sectionType?: string }).sectionType === "user" ||
    (section as { placeholder?: string }).placeholder === "USER_INPUT";
  if (!legacyUser) return section;

  let textPrefix = section.textPrefix.trim();
  if (!/\[USER_INPUT\]/i.test(textPrefix)) {
    textPrefix = textPrefix ? `${textPrefix}\n[USER_INPUT]` : "[USER_INPUT]";
  }

  return {
    ...section,
    sectionType: "static",
    placeholder: "none",
    prompt: "",
    assignedTopics: [],
    textPrefix,
    activeTab: textPrefix ? 1 : 0,
  };
}

export function normalizeTemplateSections(
  sections: TemplateSection[],
): TemplateSection[] {
  return sections.map(normalizeTemplateSection);
}

export function parseTemplateMarkdown(markdown: string): ParsedApiSection[] {
  let processed = markdown.replace(/\[([^\]]+)\]/g, (_, val) => `<p>[${val}]</p>`);
  processed = processed
    .replace(/^###### (.*)$/gm, "<h6>$1</h6>\n")
    .replace(/^##### (.*)$/gm, "<h5>$1</h5>\n")
    .replace(/^#### (.*)$/gm, "<h4>$1</h4>\n")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>\n")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>\n")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>\n");

  const sectionRegex = /<h([1-6])>([^<]+)<\/h\1>\n?([\s\S]*?)(?=<h[1-6]>|$)/g;
  const sections: ParsedApiSection[] = [];
  let match;
  let sectionId = 1;

  while ((match = sectionRegex.exec(processed)) !== null) {
    const [, lvl, heading, content] = match;
    const promptMatch = content.match(/<!--\s*PROMPT:\s*([\s\S]*?)-->/i);
    const topicsMatch = content.match(/<!--\s*ASSIGNED_TOPICS:\s*([^>]*)-->/i);
    const promptTemplate = promptMatch ? promptMatch[1].trim() : undefined;
    const assignedMainTopics = topicsMatch
      ? topicsMatch[1].split(",").map((t) => t.trim()).filter(Boolean)
      : [];
    const hasGenerated = /\[GENERATED_CONTENT\]/i.test(content);
    const type = promptTemplate && hasGenerated ? "dynamic" : "static";
    const cleaned =
      type === "dynamic"
        ? content.replace(/<!--[\s\S]*?-->/g, "")
        : content;
    const tag = `h${lvl}`;
    sections.push({
      sectionId: String(sectionId++),
      displayName: heading.trim(),
      type,
      htmlStructure: `<${tag}>${heading.trim()}</${tag}>\n${cleaned.trim()}`,
      assignedMainTopics: type === "dynamic" ? assignedMainTopics : [],
      promptTemplate: type === "dynamic" ? promptTemplate : undefined,
      description: "",
    });
  }
  return sections;
}

function serializeSection(section: TemplateSection): string {
  section = applyInferredSectionType(section);
  const hashes = "#".repeat(section.headingLevel);
  const title = section.title.trim() || "Untitled section";
  const textPrefix = normalizeTextPrefix(section.textPrefix, title);
  const useHeading = section.useSectionHeading !== false;
  let md = useHeading ? `${hashes} ${title}\n` : "";

  if (section.sectionType === "ai") {
    if (textPrefix) md += `${textPrefix}\n`;
    const promptMd = serializePromptForMarkdown(section);
    if (promptMd) md += `<!-- PROMPT: ${promptMd} -->\n`;
    if (section.assignedTopics.length) {
      md += `<!-- ASSIGNED_TOPICS: ${section.assignedTopics.join(", ")} -->\n`;
    }
    if (section.placeholder === "GENERATED_CONTENT") md += `[GENERATED_CONTENT]`;
    return md.trim();
  }

  if (section.guidanceComment) md += `${section.guidanceComment}\n`;
  if (textPrefix) md += textPrefix;
  return md.trim();
}

export function templateSectionsToMarkdown(sections: TemplateSection[]): string {
  if (!sections.length) return "";

  const parts: string[] = [];
  let i = 0;

  while (i < sections.length) {
    const section = sections[i];
    if (section.rawBlock) {
      const group = [section];
      const baseLevel = section.headingLevel;
      const baseTitle = section.title;
      let j = i + 1;
      while (j < sections.length && sections[j].rawBlock && sections[j].headingLevel > baseLevel) {
        group.push(sections[j]);
        j++;
      }
      if (group.length === 1) {
        const s = group[0];
        const body = stripDuplicateHeadingFromBlock(s.rawBlock!, s.title);
        parts.push(`${"#".repeat(s.headingLevel)} ${s.title}\n${body}`.trim());
      } else {
        const body = group.map((s) => s.rawBlock).join("\n\n");
        parts.push(`${"#".repeat(baseLevel)} ${baseTitle}\n${body}`.trim());
      }
      i = j;
      continue;
    }
    parts.push(serializeSection(section));
    i++;
  }
  return parts.join("\n\n");
}

export function sectionsToTemplateSections(sections: ParsedApiSection[]): TemplateSection[] {
  if (!sections?.length) return [createEmptySection()];
  return sections.map((section) => {
    const html = section.htmlStructure || "";
    const commentPrompt = html.match(/<!--\s*PROMPT:\s*([\s\S]*?)-->/i);
    const commentTopics = html.match(/<!--\s*ASSIGNED_TOPICS:\s*([^>]*)-->/i);
    const prompt = stripTopicDescriptionsToken(
      section.promptTemplate || (commentPrompt ? commentPrompt[1].trim() : ""),
    );
    const assignedTopics =
      section.assignedMainTopics?.length
        ? section.assignedMainTopics
        : commentTopics
          ? commentTopics[1].split(",").map((t) => t.trim()).filter(Boolean)
          : [];
    const sectionType: SectionType = prompt.trim() ? "ai" : "static";
    const placeholder: PlaceholderKind =
      sectionType === "ai" ? "GENERATED_CONTENT" : "none";
    const m = html.match(/<h([1-6])>/i);
    const level = m ? (parseInt(m[1], 10) as TemplateSection["headingLevel"]) : 2;
    const body = html
      .replace(/<h[1-6]>[^<]+<\/h[1-6]>\n?/i, "")
      .replace(/<!--[\s\S]*?-->/g, "")
      .replace(/<p>\[([^\]]+)\]<\/p>/gi, "[$1]")
      .replace(/<[^>]+>/g, "")
      .trim();
    const textPrefix = body.replace(/\[GENERATED_CONTENT\]/gi, "").trim();
    const parsed: TemplateSection = {
      id: section.sectionId || newSectionId(),
      title: section.displayName || "",
      headingLevel: level,
      sectionType,
      prompt,
      assignedTopics,
      textPrefix,
      placeholder,
      activeTab: 0,
    };
    parsed.activeTab = defaultActiveTab(parsed);
    return normalizeTemplateSection(parsed);
  });
}

function parsedSectionsLookEmpty(sections: TemplateSection[]): boolean {
  if (!sections.length) return true;
  return !sections.some(
    (s) =>
      s.title.trim() ||
      s.prompt.trim() ||
      s.textPrefix.trim() ||
      s.rawBlock?.trim(),
  );
}

function enrichParsedFromApi(
  parsed: TemplateSection[],
  apiSections: TemplateSection[],
): TemplateSection[] {
  const apiByTitle = new Map(
    apiSections.map((s) => [s.title.trim().toLowerCase(), s]),
  );
  return parsed.map((section) => {
    if (section.prompt.trim() || section.textPrefix.trim()) return section;
    const api = apiByTitle.get(section.title.trim().toLowerCase());
    if (!api) return section;
    return {
      ...section,
      prompt: api.prompt || section.prompt,
      assignedTopics: api.assignedTopics.length
        ? api.assignedTopics
        : section.assignedTopics,
      textPrefix: api.textPrefix || section.textPrefix,
      sectionType: api.sectionType !== "static" ? api.sectionType : section.sectionType,
      placeholder: api.placeholder !== "none" ? api.placeholder : section.placeholder,
      activeTab: defaultActiveTab({
        ...section,
        prompt: api.prompt || section.prompt,
        assignedTopics: api.assignedTopics.length
          ? api.assignedTopics
          : section.assignedTopics,
        textPrefix: api.textPrefix || section.textPrefix,
        sectionType:
          api.sectionType !== "static" ? api.sectionType : section.sectionType,
      }),
    };
  });
}

/** Human-readable body fallback for the section editor Text tab. */
export function sectionEditorTextValue(section: TemplateSection): string {
  if (section.textPrefix.trim()) return section.textPrefix;
  if (!section.rawBlock) return "";
  return stripMetaFromPrefix(section.rawBlock);
}

export function hydrateTemplateSections(
  rawMarkdown?: string,
  sections?: ParsedApiSection[],
): TemplateSection[] {
  const fromApi = sections?.length ? sectionsToTemplateSections(sections) : null;

  if (rawMarkdown?.trim()) {
    const parsed = parseRawMarkdownToTemplateSections(rawMarkdown);
    if (parsedSectionsLookEmpty(parsed) && fromApi?.length) {
      return normalizeTemplateSections(fromApi);
    }
    if (fromApi?.length) {
      return normalizeTemplateSections(enrichParsedFromApi(parsed, fromApi));
    }
    return parsed;
  }

  if (fromApi?.length) return normalizeTemplateSections(fromApi);
  return [createEmptySection()];
}

export interface DocTemplateListItem {
  templateId: string;
  orgId: string;
  userId?: string;
  templateName: string;
  templateType: string;
  version?: string;
  sections?: any[];
  templateContent?: string;
  rawMarkdown?: string;
  templateUploadedBy: "orgadmin" | "global admin" | "user";
  isGlobalTemplate: boolean;
  isClonedTemplate?: boolean;
  isDefaultPPT?: boolean;
  versions?: { version: string; rawMarkdown: string; updatedAt: string }[];
}

export interface DocTemplateFormData {
  templateName: string;
  templateType: string;
  templateContent: string;
  rawMarkdown?: string;
}

/** Best available markdown when syncing section builder ↔ Monaco. */
export function coalesceMarkdown(
  monacoContent: string,
  sections: TemplateSection[],
): string {
  if (monacoContent.trim()) return monacoContent;
  const fromSections = templateSectionsToMarkdown(sections);
  return fromSections.trim() ? fromSections : monacoContent;
}

export function normalizeMarkdown(md: string): string {
  return md.replace(/\r\n/g, "\n").trim();
}

export function findChangedSectionTitle(
  prev: TemplateSection[],
  next: TemplateSection[],
): string | null {
  const prevById = new Map(prev.map((s) => [s.id, s]));
  for (let i = 0; i < next.length; i++) {
    const s = next[i];
    const p = prevById.get(s.id);
    if (!p) {
      return s.title.trim() || `Section ${i + 1}`;
    }
    if (
      p.title !== s.title ||
      p.prompt !== s.prompt ||
      p.textPrefix !== s.textPrefix ||
      p.sectionType !== s.sectionType ||
      p.headingLevel !== s.headingLevel ||
      p.placeholder !== s.placeholder ||
      JSON.stringify(p.assignedTopics) !== JSON.stringify(s.assignedTopics)
    ) {
      return s.title.trim() || `Section ${i + 1}`;
    }
  }
  for (let i = 0; i < next.length; i++) {
    if (prev[i]?.id !== next[i]?.id) {
      return next[i].title.trim() || `Section ${i + 1}`;
    }
  }
  return null;
}
